// @figma file=pkCGby54zXzTsccXvHqzO6 node=10967-608729 name="DataTable" set="mytaible-shadcn-studio" verified=2026-03-24

import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../atoms/table';

// ─── Variants ─────────────────────────────────────────────────────────────────

/**
 * Row density variants for the data table.
 *
 * compact     → tighter row padding for dense data views
 * default     → standard row padding
 * comfortable → generous row padding for readability
 */
const dataTableVariants = cva('w-full', {
  variants: {
    density: {
      compact: '[&_td]:py-1 [&_td]:px-2 [&_th]:py-1 [&_th]:px-2 text-xs',
      default: '[&_td]:py-2 [&_td]:px-4 [&_th]:py-3 [&_th]:px-4 text-sm',
      comfortable: '[&_td]:py-4 [&_td]:px-4 [&_th]:py-4 [&_th]:px-4 text-sm',
    },
  },
  defaultVariants: { density: 'default' },
});

// ─── Types ────────────────────────────────────────────────────────────────────

export type DataTableDensity = 'compact' | 'default' | 'comfortable';
export type DataTableSelection = 'none' | 'single' | 'multi';

export interface DataTableProps<TData, TValue>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof dataTableVariants> {
  /** Column definitions — pass to TanStack Table */
  columns: ColumnDef<TData, TValue>[];
  /** Row data */
  data: TData[];
  /** Enable column sorting (default: true) */
  sorting?: boolean;
  /** Enable column filtering (default: true) */
  filtering?: boolean;
  /** Enable pagination (default: true) */
  pagination?: boolean;
  /** Optional per-row className callback */
  getRowClassName?: (row: TData) => string | undefined;
  /** Row selection mode (default: 'none') */
  selection?: DataTableSelection;
  /** Page size options (default: [10, 20, 30, 50]) */
  pageSizeOptions?: number[];
  /** Default page size (default: 10) */
  defaultPageSize?: number;
  /** Controlled sorting state */
  sortingState?: SortingState;
  /** Callback when sorting changes */
  onSortingChange?: (sorting: SortingState) => void;
  /** Controlled filter state */
  filterState?: ColumnFiltersState;
  /** Callback when filters change */
  onFilterChange?: (filters: ColumnFiltersState) => void;
  /** Controlled row selection state */
  selectionState?: RowSelectionState;
  /** Callback when selection changes */
  onSelectionChange?: (selection: RowSelectionState) => void;
  /** Global filter value */
  globalFilter?: string;
  /** Callback when global filter changes */
  onGlobalFilterChange?: (value: string) => void;
  /** Text shown when no data */
  emptyMessage?: string;
  /** Accessible label for the table */
  'aria-label'?: string;
  /** Initial column visibility state — use to hide columns at a given breakpoint */
  initialColumnVisibility?: VisibilityState;
  /** Controlled column visibility state — when provided, DataTable uses it directly and ignores internal toggle state */
  columnVisibilityState?: VisibilityState;
}

// ─── DataTableColumnHeader ────────────────────────────────────────────────────

export interface DataTableColumnHeaderProps extends React.HTMLAttributes<HTMLButtonElement> {
  /** Whether the column is sorted */
  sorted?: false | 'asc' | 'desc';
  /** Whether sorting is enabled for this column */
  sortable?: boolean;
  /** Callback to toggle sorting */
  onSort?: () => void;
}

/**
 * Sortable column header button with directional arrow indicator.
 * Used inside TanStack column definitions for interactive sort headers.
 */
const DataTableColumnHeader = React.forwardRef<HTMLButtonElement, DataTableColumnHeaderProps>(
  ({ className, children, sorted, sortable = true, onSort, ...props }, ref) => {
    if (!sortable) {
      return <span className={cn('flex items-center', className)}>{children}</span>;
    }

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'flex items-center gap-1 hover:text-foreground transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-[var(--radius)]',
          className,
        )}
        onClick={onSort}
        aria-label={
          sorted === 'asc'
            ? 'Sorted ascending, click to sort descending'
            : sorted === 'desc'
              ? 'Sorted descending, click to remove sort'
              : 'Click to sort ascending'
        }
        {...props}
      >
        {children}
        {sorted === 'asc' ? (
          <ArrowUpIcon className="size-4 text-foreground" aria-hidden="true" />
        ) : sorted === 'desc' ? (
          <ArrowDownIcon className="size-4 text-foreground" aria-hidden="true" />
        ) : (
          <ArrowUpDownIcon className="size-4 text-muted-foreground" aria-hidden="true" />
        )}
      </button>
    );
  },
);
DataTableColumnHeader.displayName = 'DataTableColumnHeader';

// ─── DataTablePagination ──────────────────────────────────────────────────────

interface DataTablePaginationProps {
  pageIndex: number;
  pageCount: number;
  pageSize: number;
  pageSizeOptions: number[];
  canPreviousPage: boolean;
  canNextPage: boolean;
  totalRows: number;
  selectedRows: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  selectionEnabled: boolean;
}

function DataTablePagination({
  pageIndex,
  pageCount,
  pageSize,
  pageSizeOptions,
  canPreviousPage,
  canNextPage,
  totalRows,
  selectedRows,
  onPageChange,
  onPageSizeChange,
  selectionEnabled,
}: DataTablePaginationProps) {
  return (
    <div className="flex items-center justify-between px-2 py-4">
      <div className="flex-1 text-sm text-muted-foreground">
        {selectionEnabled ? (
          <span>
            {selectedRows} of {totalRows} row(s) selected
          </span>
        ) : (
          <span>{totalRows} row(s) total</span>
        )}
      </div>

      <div className="flex items-center gap-6 lg:gap-8">
        {/* Page size selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page</span>
          <select
            className={cn(
              'h-8 w-16 rounded-[var(--field-radius,var(--radius))] border border-border bg-background px-2 text-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            )}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Page indicator */}
        <div className="flex w-24 items-center justify-center text-sm text-muted-foreground">
          Page {pageIndex + 1} of {pageCount || 1}
        </div>

        {/* Navigation buttons */}
        <nav className="flex items-center gap-1" aria-label="Table pagination">
          <button
            type="button"
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius)] border border-border bg-background transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              'disabled:pointer-events-none disabled:opacity-50',
            )}
            onClick={() => onPageChange(0)}
            disabled={!canPreviousPage}
            aria-label="Go to first page"
          >
            <ChevronsLeftIcon className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius)] border border-border bg-background transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              'disabled:pointer-events-none disabled:opacity-50',
            )}
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={!canPreviousPage}
            aria-label="Go to previous page"
          >
            <ChevronLeftIcon className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius)] border border-border bg-background transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              'disabled:pointer-events-none disabled:opacity-50',
            )}
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={!canNextPage}
            aria-label="Go to next page"
          >
            <ChevronRightIcon className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius)] border border-border bg-background transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              'disabled:pointer-events-none disabled:opacity-50',
            )}
            onClick={() => onPageChange(pageCount - 1)}
            disabled={!canNextPage}
            aria-label="Go to last page"
          >
            <ChevronsRightIcon className="size-4" aria-hidden="true" />
          </button>
        </nav>
      </div>
    </div>
  );
}

// ─── DataTable ────────────────────────────────────────────────────────────────

/**
 * Data grid molecule with sorting, filtering, and pagination.
 *
 * Built on **TanStack Table v8** + the existing Table atom primitives.
 * Supports column sorting, column/global filtering, row selection (none/single/multi),
 * pagination, and three density variants (compact/default/comfortable).
 *
 * @example
 * ```tsx
 * const columns: ColumnDef<Payment>[] = [
 *   { accessorKey: 'status', header: 'Status' },
 *   { accessorKey: 'email', header: 'Email' },
 *   { accessorKey: 'amount', header: () => <div className="text-right">Amount</div>,
 *     cell: ({ row }) => <div className="text-right">{row.getValue('amount')}</div> },
 * ];
 *
 * <DataTable columns={columns} data={payments} sorting filtering pagination />
 * ```
 */
function DataTableInner<TData, TValue>(
  {
    columns,
    data,
    sorting: sortingEnabled = true,
    filtering: filteringEnabled = true,
    pagination: paginationEnabled = true,
    selection = 'none',
    density,
    pageSizeOptions = [10, 20, 30, 50],
    defaultPageSize = 10,
    getRowClassName,
    sortingState: controlledSorting,
    onSortingChange,
    filterState: controlledFilters,
    onFilterChange,
    selectionState: controlledSelection,
    onSelectionChange,
    globalFilter: controlledGlobalFilter,
    onGlobalFilterChange,
    emptyMessage = 'No results.',
    initialColumnVisibility = {},
    columnVisibilityState: controlledColumnVisibility,
    className,
    'aria-label': ariaLabel = 'Data table',
    ...props
  }: DataTableProps<TData, TValue>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  // ── Internal state (uncontrolled mode) ──────────────────────────────────
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [internalFilters, setInternalFilters] = React.useState<ColumnFiltersState>([]);
  const [internalSelection, setInternalSelection] = React.useState<RowSelectionState>({});
  const [internalGlobalFilter, setInternalGlobalFilter] = React.useState('');
  const [paginationState, setPaginationState] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [internalColumnVisibility, setInternalColumnVisibility] =
    React.useState<VisibilityState>(initialColumnVisibility);
  const columnVisibility = controlledColumnVisibility ?? internalColumnVisibility;

  const handleColumnVisibilityChange = React.useCallback(
    (updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
      // In controlled mode the external owner manages state — ignore TanStack's internal changes.
      if (controlledColumnVisibility !== undefined) return;
      const next = typeof updater === 'function' ? updater(internalColumnVisibility) : updater;
      setInternalColumnVisibility(next);
    },
    [controlledColumnVisibility, internalColumnVisibility],
  );

  // Resolve controlled vs uncontrolled
  const sortingValue = controlledSorting ?? internalSorting;
  const filtersValue = controlledFilters ?? internalFilters;
  const selectionValue = controlledSelection ?? internalSelection;
  const globalFilterValue = controlledGlobalFilter ?? internalGlobalFilter;

  const handleSortingChange = React.useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      const next = typeof updater === 'function' ? updater(sortingValue) : updater;
      setInternalSorting(next);
      onSortingChange?.(next);
    },
    [sortingValue, onSortingChange],
  );

  const handleFilterChange = React.useCallback(
    (updater: ColumnFiltersState | ((prev: ColumnFiltersState) => ColumnFiltersState)) => {
      const next = typeof updater === 'function' ? updater(filtersValue) : updater;
      setInternalFilters(next);
      onFilterChange?.(next);
    },
    [filtersValue, onFilterChange],
  );

  const handleSelectionChange = React.useCallback(
    (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      const next = typeof updater === 'function' ? updater(selectionValue) : updater;
      if (selection === 'single') {
        // Only keep the last selected row
        const keys = Object.keys(next).filter((k) => next[k]);
        const singleSelection = keys.length > 0 ? { [keys[keys.length - 1]]: true } : {};
        setInternalSelection(singleSelection);
        onSelectionChange?.(singleSelection);
      } else {
        setInternalSelection(next);
        onSelectionChange?.(next);
      }
    },
    [selectionValue, selection, onSelectionChange],
  );

  const handleGlobalFilterChange = React.useCallback(
    (value: string) => {
      setInternalGlobalFilter(value);
      onGlobalFilterChange?.(value);
    },
    [onGlobalFilterChange],
  );

  // ── Build table options ─────────────────────────────────────────────────
  const tableOptions: TableOptions<TData> = {
    data,
    columns,
    state: {
      sorting: sortingEnabled ? sortingValue : undefined,
      columnFilters: filteringEnabled ? filtersValue : undefined,
      columnVisibility,
      rowSelection: selection !== 'none' ? selectionValue : {},
      globalFilter: filteringEnabled ? globalFilterValue : undefined,
      pagination: paginationEnabled
        ? paginationState
        : { pageIndex: 0, pageSize: Number.MAX_SAFE_INTEGER },
    },
    enableRowSelection: selection !== 'none',
    enableMultiRowSelection: selection === 'multi',
    onSortingChange: sortingEnabled
      ? (handleSortingChange as TableOptions<TData>['onSortingChange'])
      : undefined,
    onColumnFiltersChange: filteringEnabled
      ? (handleFilterChange as TableOptions<TData>['onColumnFiltersChange'])
      : undefined,
    onColumnVisibilityChange:
      handleColumnVisibilityChange as TableOptions<TData>['onColumnVisibilityChange'],
    onRowSelectionChange:
      selection !== 'none'
        ? (handleSelectionChange as TableOptions<TData>['onRowSelectionChange'])
        : undefined,
    onGlobalFilterChange: filteringEnabled ? handleGlobalFilterChange : undefined,
    ...(paginationEnabled ? { onPaginationChange: setPaginationState } : {}),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  };

  const table = useReactTable(tableOptions);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div ref={ref} className={cn('space-y-4', className)} {...props}>
      {/* Global filter input */}
      {filteringEnabled && (
        <div className="flex items-center">
          <input
            type="text"
            placeholder="Filter..."
            value={globalFilterValue}
            onChange={(e) => handleGlobalFilterChange(e.target.value)}
            className={cn(
              'h-9 w-full max-w-sm rounded-[var(--field-radius,var(--radius))] border border-border bg-background px-3 text-sm',
              'placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            )}
            aria-label="Filter table data"
          />
        </div>
      )}

      {/* Table */}
      <div
        className={cn(
          'rounded-[var(--radius)] border border-border',
          dataTableVariants({ density }),
        )}
      >
        <Table aria-label={ariaLabel}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : sortingEnabled && header.column.getCanSort() ? (
                      <DataTableColumnHeader
                        sorted={header.column.getIsSorted()}
                        onSort={() => header.column.toggleSorting()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </DataTableColumnHeader>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  aria-selected={row.getIsSelected() || undefined}
                  onClick={
                    selection !== 'none'
                      ? () => row.toggleSelected(!row.getIsSelected())
                      : undefined
                  }
                  className={cn(
                    selection !== 'none' && 'cursor-pointer',
                    getRowClassName?.(row.original),
                  )}
                  role={selection !== 'none' ? 'row' : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {paginationEnabled && (
        <DataTablePagination
          pageIndex={table.getState().pagination.pageIndex}
          pageCount={table.getPageCount()}
          pageSize={table.getState().pagination.pageSize}
          pageSizeOptions={pageSizeOptions}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
          totalRows={table.getFilteredRowModel().rows.length}
          selectedRows={table.getSelectedRowModel().rows.length}
          onPageChange={(page) => table.setPageIndex(page)}
          onPageSizeChange={(size) => table.setPageSize(size)}
          selectionEnabled={selection !== 'none'}
        />
      )}
    </div>
  );
}

// Cast to preserve generics through forwardRef
const DataTable = React.forwardRef(DataTableInner) as <TData, TValue>(
  props: DataTableProps<TData, TValue> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;

(DataTable as React.FC).displayName = 'DataTable';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { DataTable, DataTableColumnHeader, DataTablePagination, dataTableVariants };
